import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { CheckCircle, XCircle, Clock, AlertCircle, ArrowLeft, ChevronDown, ChevronRight, Image, X, Loader2, Square, RotateCcw } from 'lucide-react';
import clsx from 'clsx';
import { apiService, getApiBaseUrl } from '../services/api';
import JiraTicketAction from '../components/JiraTicketAction';

const API = getApiBaseUrl();
const POLL_INTERVAL = 3000; // 3 seconds

const TestRunDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [testRun, setTestRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [screenshotModal, setScreenshotModal] = useState(null);
  const [journeys, setJourneys] = useState(null);
  const [showPassing, setShowPassing] = useState({}); // per-journey "show passing" toggle
  const [activeCategory, setActiveCategory] = useState(null);
  const [stopping, setStopping] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const pollRef = useRef(null);

  // Fetch data (reusable for polling)
  const fetchData = useCallback(async () => {
    try {
      const runRes = await apiService.getTestRun(id);
      setTestRun(runRes.data);
      return runRes.data;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      return null;
    }
  }, [id]);

  // Initial load
  useEffect(() => {
    fetchData().finally(() => setLoading(false));
  }, [fetchData]);

  // Fetch journey breakdown — refresh when run finishes (status changes from running)
  useEffect(() => {
    if (!id) return;
    apiService.getTestRunJourneys(id)
      .then((res) => setJourneys(res.data))
      .catch(() => setJourneys(null));
  }, [id, testRun?.status]);

  // If a ?category= query param is present, auto-open that category once journeys load
  useEffect(() => {
    const queryCat = searchParams.get('category');
    if (queryCat && journeys && journeys[queryCat] && !activeCategory) {
      setActiveCategory(queryCat);
    }
  }, [journeys, searchParams, activeCategory]);

  // Live polling while status is "running"
  useEffect(() => {
    if (!testRun || testRun.status !== 'running') {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }

    pollRef.current = setInterval(async () => {
      const data = await fetchData();
      // Stop polling when run is no longer "running"
      if (data && data.status !== 'running') {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, POLL_INTERVAL);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [testRun?.status, fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="h-10 w-10 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  const { results } = testRun;

  const handleStop = async () => {
    if (stopping) return;
    setStopping(true);
    try {
      await apiService.cancelTestRun(id);
    } catch {
      setStopping(false);
    }
  };

  const handleRerunFailed = async () => {
    if (rerunning) return;
    setRerunning(true);
    try {
      const res = await apiService.rerunFailedTests(id);
      const taskId = res.data.task_id;
      navigate(`/test-runner?taskId=${taskId}`);
    } catch (err) {
      setRerunning(false);
      alert(err.response?.data?.detail || 'Failed to re-run tests');
    }
  };

  const counts = {
    all: results.length,
    passed: results.filter((r) => r.status === 'passed').length,
    failed: results.filter((r) => r.status === 'failed').length,
    skipped: results.filter((r) => r.status === 'skipped').length,
  };

  const passRate = counts.all > 0 ? ((counts.passed / counts.all) * 100).toFixed(0) : 0;

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/test-runs"
        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        All test runs
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <StatusBadge status={testRun.status} large />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Test Run #{testRun.id}
            </h1>
            <p className="text-sm text-gray-500">
              {format(new Date(testRun.timestamp), 'MMMM dd, yyyy HH:mm:ss')}
            </p>
            {testRun.cloudflare_blocked_tests > 0 && (
              <p className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                ⚠ Cloudflare blocked {testRun.cloudflare_blocked_tests} test{testRun.cloudflare_blocked_tests === 1 ? '' : 's'} — site bot-protection, not real failures
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-6 text-sm">
          <Stat label="Duration" value={testRun.duration ? `${(testRun.duration / 1000).toFixed(1)}s` : '-'} />
          <Stat label="Browser" value={testRun.browser} />
          <Stat label="Pass Rate" value={`${passRate}%`} />
          {counts.failed > 0 && testRun.status !== 'running' && (
            <button
              onClick={handleRerunFailed}
              disabled={rerunning}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-error-300 text-error-700 bg-error-50 hover:bg-error-100 disabled:opacity-50 transition-colors"
            >
              <RotateCcw className={clsx('h-4 w-4', rerunning && 'animate-spin')} />
              {rerunning ? 'Starting…' : `Re-run ${counts.failed} Failed`}
            </button>
          )}
        </div>
      </div>

      {/* Live indicator */}
      {testRun.status === 'running' && (() => {
        const completed = counts.passed + counts.failed + counts.skipped;
        const total = testRun.total_tests || 0;
        const allDone = total > 0 && completed >= total;
        return (
          <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 text-primary-600 animate-spin" />
                <div>
                  <h3 className="text-sm font-medium text-primary-800">
                    {allDone ? 'Finalizing Results…' : 'Tests Running — Live Results'}
                  </h3>
                  <p className="text-xs text-primary-600 mt-0.5">
                    Auto-refreshing every {POLL_INTERVAL / 1000}s &middot; {completed} of {total || '?'} completed
                  </p>
                </div>
              </div>
              {!allDone && (
                <button
                  onClick={handleStop}
                  disabled={stopping}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-error-300 text-error-700 bg-error-50 hover:bg-error-100 disabled:opacity-50 transition-colors"
                >
                  <Square className="h-3 w-3" />
                  {stopping ? 'Stopping…' : 'Stop Run'}
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Summary bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-4 gap-4 text-center">
          <MiniStat label="Total" value={counts.all} />
          <MiniStat label="Passed" value={counts.passed} color="success" />
          <MiniStat label="Failed" value={counts.failed} color="error" />
          <MiniStat label="Skipped" value={counts.skipped} color="warning" />
        </div>
        {/* Progress bar */}
        {counts.all > 0 && (
          <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden flex">
            <div
              className="bg-success-500 transition-all"
              style={{ width: `${(counts.passed / counts.all) * 100}%` }}
            />
            <div
              className="bg-error-500 transition-all"
              style={{ width: `${(counts.failed / counts.all) * 100}%` }}
            />
            <div
              className="bg-gray-300 transition-all"
              style={{ width: `${(counts.skipped / counts.all) * 100}%` }}
            />
          </div>
        )}
      </div>

      {/* Director-friendly: failure summary + 3 big category cards + drill-down */}
      {journeys && (() => {
        // Per-category aggregated stats
        const catData = Object.entries(journeys).map(([cat, jmap]) => {
          const journeyArr = Object.entries(jmap).map(([n, j]) => ({ name: n, ...j }));
          const passed = journeyArr.reduce((a, j) => a + j.passed, 0);
          const failed = journeyArr.reduce((a, j) => a + j.failed, 0);
          const skipped = journeyArr.reduce((a, j) => a + j.skipped, 0);
          const total = passed + failed + skipped;
          // Roll up status
          const hasRed = journeyArr.some((j) => j.status === 'red');
          const hasYellow = journeyArr.some((j) => j.status === 'yellow');
          const hasGreen = journeyArr.some((j) => j.status === 'green');
          let status = 'grey';
          if (hasRed) status = 'red';
          else if (hasYellow) status = 'yellow';
          else if (hasGreen) status = 'green';
          return { cat, journeys: journeyArr, passed, failed, skipped, total, status };
        }).filter((c) => c.total > 0);

        // One line per failed test, with category + journey context + friendly reason + screenshot
        const failureLines = [];
        catData.forEach((c) => {
          c.journeys.forEach((j) => {
            (j.failed_tests || []).forEach((t) => {
              failureLines.push({
                cat: c.cat,
                journey: j.name,
                title: t.title,
                id: t.id,
                summary: t.summary,
                summaryArea: t.summary_area,
                summaryView: t.summary_view,
                summarySteps: t.summary_steps,
                errorFriendly: t.error_friendly,
                screenshotPath: t.screenshot_path,
              });
            });
          });
        });

        return (
          <div className="space-y-5">
            {/* Failure summary at top — one line per failed test */}
            {failureLines.length > 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-semibold text-gray-700 mb-3">
                  Failure summary <span className="text-gray-400 font-normal">({failureLines.length} failed)</span>
                </h2>
                <ul className="space-y-2.5 max-h-[28rem] overflow-y-auto pr-1">
                  {failureLines.map((f, i) => (
                    <li key={`${f.id}-${i}`} className="flex items-start gap-2">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-error-500 shrink-0 mt-2" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-gray-700">
                          <span className="font-medium text-error-700">{f.title}</span>
                          <span className="text-gray-500"> — from </span>
                          <span className="font-medium text-gray-800">{f.journey}</span>
                          <span className="text-gray-500"> under </span>
                          <span className="font-medium text-gray-800">{f.cat}</span>
                        </div>
                        <SummaryBlock area={f.summaryArea} view={f.summaryView} summary={f.summary} steps={f.summarySteps} className="mt-1" />
                        {f.errorFriendly && (
                          <div className="text-xs text-error-700 mt-0.5">{f.errorFriendly}</div>
                        )}
                        <div className="flex flex-wrap items-center gap-2 mt-1.5">
                          {f.screenshotPath && (
                            <button
                              onClick={() => setScreenshotModal(f.screenshotPath)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border border-error-300 bg-white text-error-700 hover:bg-error-50 transition-colors"
                              title="View screenshot of the failure"
                            >
                              <Image className="h-3 w-3" /> View screenshot
                            </button>
                          )}
                          <JiraTicketAction
                            cacheKey={`web-${f.id}`}
                            fetchSuggestion={() => apiService.getJiraSuggestion(f.id)}
                            createTicket={() => apiService.createJiraTicket(f.id)}
                          />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="bg-success-50 border border-success-200 rounded-xl p-5 text-center">
                <CheckCircle className="h-6 w-6 text-success-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-success-700">All systems healthy — no failures in this run</p>
              </div>
            )}

            {/* 3 big category cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {catData.map((c) => (
                <CategoryCard
                  key={c.cat}
                  category={c.cat}
                  passed={c.passed}
                  failed={c.failed}
                  skipped={c.skipped}
                  total={c.total}
                  status={c.status}
                  active={activeCategory === c.cat}
                  onClick={() => setActiveCategory(activeCategory === c.cat ? null : c.cat)}
                />
              ))}
            </div>

            {/* Journey cards for the active category — wrapped so it's obvious what's inside */}
            {activeCategory && (() => {
              const active = catData.find((c) => c.cat === activeCategory);
              if (!active) return null;
              const visible = active.journeys.filter((j) => j.passed + j.failed + j.skipped > 0);
              const theme = STATUS_THEME[active.status] || STATUS_THEME.grey;
              return (
                <div className={clsx('rounded-xl border-2 bg-white overflow-hidden', theme.border)}>
                  <div className={clsx('px-5 py-4 border-b flex items-center justify-between', theme.border, theme.bg)}>
                    <div className="flex items-center gap-3">
                      <h2 className="text-lg font-bold text-gray-900">
                        {active.cat} — Categorical Breakdown
                      </h2>
                      <span className={clsx('text-[11px] font-semibold px-2.5 py-0.5 rounded-full tracking-wide', theme.strong, theme.text)}>
                        {theme.label}
                      </span>
                    </div>
                    <button
                      onClick={() => setActiveCategory(null)}
                      className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
                    >
                      <X className="h-3.5 w-3.5" /> Close
                    </button>
                  </div>
                  <div className="p-5">
                    <p className="text-xs text-gray-500 mb-4">
                      Each card below is one section of {active.cat}. Red = something broken, yellow = some failures, green = healthy.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {visible.map((j) => (
                        <JourneyCard
                          key={j.name}
                          name={j.name}
                          data={j}
                          onScreenshot={(p) => setScreenshotModal(p)}
                          showPassing={!!showPassing[`${active.cat}|${j.name}`]}
                          togglePassing={() => setShowPassing((prev) => ({
                            ...prev,
                            [`${active.cat}|${j.name}`]: !prev[`${active.cat}|${j.name}`],
                          }))}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* Screenshot modal */}
      {screenshotModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setScreenshotModal(null)}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] bg-white rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setScreenshotModal(null)}
              className="absolute top-3 right-3 p-1 rounded-full bg-black/40 text-white hover:bg-black/60 z-10"
            >
              <X className="h-4 w-4" />
            </button>
            <img
              src={`${API}/api/screenshots/${screenshotModal}`}
              alt="Test screenshot"
              onError={(e) => { e.target.alt = 'Screenshot not found'; }}
              className="max-w-full max-h-[90vh] object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};

/* ---- Helpers ---- */

function StatusIcon({ status, className }) {
  const icons = {
    passed: <CheckCircle className={clsx('h-4 w-4 text-success-500', className)} />,
    failed: <XCircle className={clsx('h-4 w-4 text-error-500', className)} />,
    skipped: <Clock className={clsx('h-4 w-4 text-warning-500', className)} />,
  };
  return icons[status] || <AlertCircle className={clsx('h-4 w-4 text-gray-400', className)} />;
}

function StatusBadge({ status, large }) {
  const styles = {
    passed: 'bg-success-50 text-success-700 border-success-200',
    failed: 'bg-error-50 text-error-700 border-error-200',
    partial: 'bg-orange-50 text-orange-700 border-orange-200',
    skipped: 'bg-warning-50 text-warning-700 border-warning-200',
    running: 'bg-primary-50 text-primary-700 border-primary-200',
  };
  return (
    <span className={clsx(
      'inline-flex items-center border rounded-full capitalize font-medium',
      large ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs',
      styles[status] || 'bg-gray-50 text-gray-600 border-gray-200'
    )}>
      {status}
    </span>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="font-medium text-gray-800 capitalize">{value}</p>
    </div>
  );
}

const STATUS_THEME = {
  green:  { bg: 'bg-success-50', border: 'border-success-300', strong: 'bg-success-100', text: 'text-success-700', label: 'OK' },
  yellow: { bg: 'bg-warning-50', border: 'border-warning-300', strong: 'bg-warning-100', text: 'text-warning-700', label: 'WARNING' },
  red:    { bg: 'bg-error-50',   border: 'border-error-300',   strong: 'bg-error-100',   text: 'text-error-700',   label: 'CRITICAL' },
  grey:   { bg: 'bg-gray-50',    border: 'border-gray-200',    strong: 'bg-gray-100',    text: 'text-gray-500',    label: 'NO TESTS' },
};

function FailedTestCard({ test, onScreenshot }) {
  const [showRaw, setShowRaw] = useState(false);
  return (
    <div className="bg-white rounded-lg border border-error-200 p-2.5">
      <div className="flex items-start gap-1.5 min-w-0">
        <XCircle className="h-3.5 w-3.5 text-error-500 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-gray-800">{test.title}</p>
          <SummaryBlock area={test.summary_area} view={test.summary_view} summary={test.summary} steps={test.summary_steps} className="mt-0.5" />
          {test.error_friendly && (
            <p className="text-[11px] text-error-700 mt-0.5">{test.error_friendly}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {test.screenshot_path && onScreenshot && (
              <button
                onClick={() => onScreenshot(test.screenshot_path)}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border border-error-300 bg-white text-error-700 hover:bg-error-50 transition-colors"
                title="View screenshot of the failure"
              >
                <Image className="h-3 w-3" /> View screenshot
              </button>
            )}
            {test.error_raw && (
              <button
                onClick={() => setShowRaw((v) => !v)}
                className="text-[10px] text-gray-500 hover:text-gray-700 underline"
              >
                {showRaw ? 'Hide technical details' : 'Show technical details'}
              </button>
            )}
          </div>
          {showRaw && test.error_raw && (
            <pre className="mt-1.5 text-[10px] text-gray-700 bg-gray-50 border border-gray-200 rounded p-2 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
              {test.error_raw}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryCard({ category, passed, failed, skipped, total, status, active, onClick }) {
  const theme = STATUS_THEME[status] || STATUS_THEME.grey;
  const passRate = total > 0 ? Math.round((passed / total) * 100) : 0;
  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left rounded-xl border-2 p-5 transition-all',
        theme.bg,
        active ? theme.border : 'border-transparent',
        'hover:shadow-md'
      )}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <h2 className="text-lg font-bold text-gray-900">{category}</h2>
        <span className={clsx('text-[10px] font-semibold px-2 py-0.5 rounded-full tracking-wide shrink-0', theme.strong, theme.text)}>
          {theme.label}
        </span>
      </div>
      <div className="space-y-1">
        <div className="flex items-baseline gap-2">
          <span className={clsx('text-3xl font-bold', theme.text)}>{passRate}%</span>
          <span className="text-xs text-gray-500">pass rate</span>
        </div>
        <div className="text-xs text-gray-600 flex items-center gap-3 flex-wrap">
          <span className="text-success-700">{passed} passed</span>
          {failed > 0 && <span className="text-error-700 font-medium">{failed} failed</span>}
          {skipped > 0 && <span className="text-gray-500">{skipped} skipped</span>}
        </div>
        <p className="text-[11px] text-gray-500 mt-2">
          {active ? 'Click to collapse' : 'Click to drill down'}
        </p>
      </div>
    </button>
  );
}

function SummaryBlock({ area, view, summary, steps, className }) {
  const hasSteps = Array.isArray(steps) && steps.length > 0;
  if (!area && !summary && !hasSteps) return null;
  return (
    <div className={clsx('space-y-1', className)}>
      {(area || view) && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {area && <span className="text-[11px] font-semibold text-gray-700">{area}</span>}
          {view && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 lowercase">
              {view}
            </span>
          )}
        </div>
      )}
      {summary && <p className="text-[11px] text-gray-600 leading-snug">{summary}</p>}
      {hasSteps && (
        <div>
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Steps</p>
          <ol className="list-decimal ml-4 mt-0.5 space-y-0.5 text-[11px] text-gray-500 marker:text-gray-400">
            {steps.map((s, i) => <li key={i} className="leading-snug">{s}</li>)}
          </ol>
        </div>
      )}
    </div>
  );
}

function PassingTestRow({ test, onScreenshot }) {
  const [open, setOpen] = useState(false);
  const hasSummary = !!test.summary;
  return (
    <li className="text-[11px] text-gray-600">
      <div className="flex items-start gap-1.5">
        <CheckCircle className="h-3 w-3 text-success-500 mt-0.5 shrink-0" />
        {hasSummary ? (
          <button
            onClick={() => setOpen((v) => !v)}
            className="flex items-start gap-1 flex-1 min-w-0 text-left hover:text-gray-900"
            title={open ? 'Hide summary' : 'Show what this test does'}
          >
            {open
              ? <ChevronDown className="h-3 w-3 mt-0.5 shrink-0 text-gray-400" />
              : <ChevronRight className="h-3 w-3 mt-0.5 shrink-0 text-gray-400" />}
            <span className={open ? 'flex-1' : 'truncate flex-1'}>{test.title}</span>
          </button>
        ) : (
          <span className="truncate flex-1">{test.title}</span>
        )}
        {test.screenshot_path && onScreenshot && (
          <button
            onClick={() => onScreenshot(test.screenshot_path)}
            className="p-0.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 shrink-0"
            title="View screenshot"
          >
            <Image className="h-3 w-3" />
          </button>
        )}
      </div>
      {hasSummary && open && (
        <div className="ml-[1.125rem] mb-1">
          <SummaryBlock area={test.summary_area} view={test.summary_view} summary={test.summary} steps={test.summary_steps} />
        </div>
      )}
    </li>
  );
}

function SkippedTestRow({ test }) {
  const [open, setOpen] = useState(false);
  const hasSummary = !!test.summary;
  return (
    <li className="text-[11px] text-gray-500">
      <div className="flex items-start gap-1.5">
        <Clock className="h-3 w-3 mt-0.5 shrink-0" />
        {hasSummary ? (
          <button
            onClick={() => setOpen((v) => !v)}
            className="flex items-start gap-1 flex-1 min-w-0 text-left hover:text-gray-700"
            title={open ? 'Hide summary' : 'Show what this test does'}
          >
            {open
              ? <ChevronDown className="h-3 w-3 mt-0.5 shrink-0 text-gray-400" />
              : <ChevronRight className="h-3 w-3 mt-0.5 shrink-0 text-gray-400" />}
            <span className={open ? 'flex-1' : 'truncate flex-1'}>
              {test.title} <span className="text-gray-400">(skipped)</span>
            </span>
          </button>
        ) : (
          <span className="truncate flex-1">{test.title} <span className="text-gray-400">(skipped)</span></span>
        )}
      </div>
      {/* Why it skipped — surfaced so skips aren't a black box like failures aren't. */}
      {test.skip_reason && (
        <p className="ml-[1.125rem] mt-0.5 text-[11px] text-warning-700">
          Skipped: {test.skip_reason}
        </p>
      )}
      {hasSummary && open && (
        <div className="ml-[1.125rem] mb-1 mt-1">
          <SummaryBlock area={test.summary_area} view={test.summary_view} summary={test.summary} steps={test.summary_steps} />
        </div>
      )}
    </li>
  );
}

function JourneyCard({ name, data, onScreenshot, showPassing, togglePassing }) {
  const theme = STATUS_THEME[data.status] || STATUS_THEME.grey;
  const total = data.passed + data.failed + data.skipped;
  return (
    <div className={clsx('rounded-xl border p-4', theme.bg, theme.border)}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold text-gray-900">{name}</h3>
        <span className={clsx('text-[10px] font-semibold px-2 py-0.5 rounded-full tracking-wide shrink-0', theme.strong, theme.text)}>
          {theme.label}
        </span>
      </div>
      <p className="text-xs text-gray-600 mb-3">
        {data.failed > 0
          ? <><span className="font-medium text-error-700">{data.failed} failed</span> of {total} tests</>
          : <>{data.passed} of {total} tests passing{data.skipped ? `, ${data.skipped} skipped` : ''}</>}
      </p>

      {/* Failed tests */}
      {data.failed_tests.length > 0 && (
        <div className="space-y-2 mb-3">
          {data.failed_tests.map((t) => (
            <FailedTestCard key={t.id} test={t} onScreenshot={onScreenshot} />
          ))}
        </div>
      )}

      {/* Skipped tests */}
      {data.skipped_tests.length > 0 && (
        <ul className="space-y-1 mb-3">
          {data.skipped_tests.map((t) => (
            <SkippedTestRow key={t.id} test={t} onScreenshot={onScreenshot} />
          ))}
        </ul>
      )}

      {/* Show passing tests */}
      {data.passed > 0 && (
        <div>
          <button
            onClick={togglePassing}
            className={clsx('text-[11px] font-medium', theme.text, 'hover:underline')}
          >
            {showPassing ? 'Hide' : 'Show'} {data.passed} passing test{data.passed === 1 ? '' : 's'}
          </button>
          {showPassing && (
            <ul className="mt-2 space-y-1">
              {data.passed_tests.map((t) => (
                <PassingTestRow key={t.id} test={t} onScreenshot={onScreenshot} />
              ))}
            </ul>
          )}
        </div>
      )}

      {/* All-passing case */}
      {data.failed === 0 && data.skipped === 0 && (
        <p className={clsx('text-xs italic', theme.text)}>All {data.passed} tests passing</p>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    green:  { bg: 'bg-success-100', text: 'text-success-700', label: 'OK' },
    yellow: { bg: 'bg-warning-100', text: 'text-warning-700', label: 'WARNING' },
    red:    { bg: 'bg-error-100',   text: 'text-error-700',   label: 'CRITICAL' },
    grey:   { bg: 'bg-gray-100',    text: 'text-gray-500',    label: 'NO TESTS' },
  };
  const s = map[status] || map.grey;
  return (
    <span className={clsx('inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full tracking-wide', s.bg, s.text)}>
      {s.label}
    </span>
  );
}

function FailedTestRow({ test, onScreenshot }) {
  return (
    <div className="ml-7 rounded-lg border border-error-200 bg-error-50/40 px-3 py-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <XCircle className="h-4 w-4 text-error-500 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-800">{test.title}</p>
            {test.error_friendly && (
              <p className="text-xs text-error-700 mt-0.5">{test.error_friendly}</p>
            )}
            {test.suite && (
              <p className="text-[11px] text-gray-400 mt-0.5">{test.suite}</p>
            )}
          </div>
        </div>
        {test.screenshot_path && onScreenshot && (
          <button
            onClick={() => onScreenshot(test.screenshot_path)}
            className="p-1 rounded hover:bg-white text-gray-400 hover:text-gray-600 shrink-0"
            title="View screenshot"
          >
            <Image className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  const textColor = {
    success: 'text-success-600',
    error: 'text-error-600',
    warning: 'text-warning-600',
  }[color] || 'text-gray-800';

  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className={clsx('text-xl font-bold', textColor)}>{value}</p>
    </div>
  );
}

export default TestRunDetail;
